import axios from 'axios';
import { createCloudRunAxios, getCloudRunServiceUrl } from './axios';

// Define base URLs for services (environment-aware)
const CUSTOM_FIELDS_MANAGER_URL = getCloudRunServiceUrl('fub-custom-fields-manager');
const STAGE_MANAGER_URL = getCloudRunServiceUrl('fub-stage-manager');
const TEAM_MEMBERS_URL = getCloudRunServiceUrl('fub-users-to-team-members');
const WEBHOOK_MANAGER_URL = getCloudRunServiceUrl('fub-webhook-manager');

const remiApi = createCloudRunAxios('remi-api');

/**
 * Store a Follow Up Boss API key for an organization
 * @param orgId The organization ID
 * @param apiKey The API key to store
 * @returns A promise that resolves to a boolean indicating success
 */
export const storeFollowUpBossApiKey = async (orgId: string, apiKey: string): Promise<boolean> => {
  try {
    const response = await remiApi.post('/api/v1/integrations/crm/credentials/upload', {
      orgId,
      apiKey,
      source: 'followupboss',
    });

    return response.data.status === 'success';
  } catch (error) {
    console.error('Error storing Follow Up Boss API key:', error);
    throw error;
  }
};

/**
 * Setup custom fields in Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the setup results
 */
export const setupCustomFields = async (orgId: string) => {
  try {
    const response = await axios.post(`${CUSTOM_FIELDS_MANAGER_URL}/org/${orgId}/setup`, {});

    return response.data;
  } catch (error) {
    console.error('Error setting up custom fields:', error);
    throw error;
  }
};

/**
 * Get custom fields from Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the custom fields
 */
export const getCustomFields = async (orgId: string) => {
  try {
    // Try the new endpoint first, fall back to the old one if it fails
    try {
      const response = await axios.get(`${CUSTOM_FIELDS_MANAGER_URL}/org/${orgId}/fields`);
      return response.data;
    } catch (fieldError) {
      // Fall back to the original endpoint
      const response = await axios.get(`${CUSTOM_FIELDS_MANAGER_URL}/org/${orgId}/custom-fields`);
      return response.data;
    }
  } catch (error) {
    console.error('Error getting custom fields:', error);
    throw error;
  }
};

/**
 * Get required fields configuration for Follow Up Boss
 * @param orgId The organization ID
 * @returns A promise that resolves to the required fields configuration
 */
export const getRequiredFields = async (orgId: string) => {
  try {
    const response = await axios.get(`${CUSTOM_FIELDS_MANAGER_URL}/org/${orgId}/required`);
    return response.data;
  } catch (error) {
    console.error('Error getting required fields configuration:', error);
    throw error;
  }
};

/**
 * Create a specific custom field in Follow Up Boss
 * @param orgId The organization ID
 * @param fieldData The field data (name, type, label)
 * @returns A promise that resolves to the created field
 */
export const createCustomField = async (
  orgId: string,
  fieldData: { name: string; type: string; label: string; choices?: string[] }
) => {
  try {
    const response = await axios.post(`${CUSTOM_FIELDS_MANAGER_URL}/org/${orgId}/field`, fieldData);
    return response.data;
  } catch (error) {
    console.error('Error creating custom field:', error);
    throw error;
  }
};

/**
 * Update custom fields for a specific contact in Follow Up Boss
 * @param orgId The organization ID
 * @param personId The person ID
 * @param customFields The custom fields to update
 * @returns A promise that resolves to the update result
 */
export const updateContactCustomFields = async (
  orgId: string,
  personId: string,
  customFields: Record<string, string | number | boolean | null>
) => {
  try {
    const response = await axios.put(
      `${CUSTOM_FIELDS_MANAGER_URL}/org/${orgId}/contact/${personId}`,
      customFields
    );
    return response.data;
  } catch (error) {
    console.error('Error updating contact custom fields:', error);
    throw error;
  }
};

/**
 * Validate custom fields in Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the validation results
 */
export const validateCustomFields = async (orgId: string) => {
  try {
    const response = await axios.get(`${CUSTOM_FIELDS_MANAGER_URL}/org/${orgId}/validate`);

    // Convert the new format to the format expected by the UI
    const data = response.data;
    return {
      success: data.success,
      validation: {
        missing_fields: data.validation.missing || [],
        existing_fields: data.validation.configured || [],
        all_fields_present: data.validation.valid,
      },
    };
  } catch (error) {
    console.error('Error validating custom fields:', error);
    throw error;
  }
};

/**
 * Pull stages from Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the stages
 */
export const pullStages = async (orgId: string) => {
  try {
    const response = await axios.get(`${STAGE_MANAGER_URL}/stages/${orgId}`);

    return response.data;
  } catch (error) {
    console.error('Error pulling stages:', error);
    throw error;
  }
};

/**
 * Get stage allowlist from Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the allowlist
 */
export const getStageAllowlist = async (orgId: string) => {
  try {
    const response = await axios.get(`${STAGE_MANAGER_URL}/allowlist/${orgId}`);

    return response.data;
  } catch (error) {
    console.error('Error getting stage allowlist:', error);
    throw error;
  }
};

/**
 * Set stage allowlist for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @param allowedStages Array of stage names to allow
 * @returns A promise that resolves to the updated allowlist
 */
export const setStageAllowlist = async (orgId: string, allowedStages: string[]) => {
  try {
    const response = await axios.post(`${STAGE_MANAGER_URL}/allowlist/${orgId}`, {
      allowedStages,
    });

    return response.data;
  } catch (error) {
    console.error('Error setting stage allowlist:', error);
    throw error;
  }
};

/**
 * Check if a stage is allowed for AI engagement
 * @param orgId The organization ID
 * @param stageName The stage name to check
 * @returns A promise that resolves to the check result
 */
export const checkStagePermission = async (orgId: string, stageName: string) => {
  try {
    const response = await axios.get(
      `${STAGE_MANAGER_URL}/check/${orgId}/${encodeURIComponent(stageName)}`
    );

    // Handle both old and new response formats
    const data = response.data;
    if (data.success) {
      // If the response has the new format with isAllowed, return it directly
      if ('isAllowed' in data) {
        return data;
      }
      // If it has the old format with allowed, convert it to the new format
      if ('allowed' in data) {
        return {
          success: data.success,
          orgId: data.org_id || orgId,
          stageName: data.stage || stageName,
          isAllowed: data.allowed,
        };
      }
    }
    return data;
  } catch (error) {
    console.error('Error checking stage permission:', error);
    throw error;
  }
};

/**
 * Pull lead sources from Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the lead sources
 */
export const pullLeadSources = async (orgId: string) => {
  try {
    const response = await axios.get(`${STAGE_MANAGER_URL}/sources/${orgId}`);

    return response.data;
  } catch (error) {
    console.error('Error pulling lead sources:', error);
    throw error;
  }
};

/**
 * Sync team members from Follow Up Boss to Speculo
 * @param orgId The organization ID
 * @param createMissing Whether to create missing team members
 * @returns A promise that resolves to the sync results
 */
export const syncTeamMembers = async (orgId: string, createMissing: boolean = true) => {
  try {
    const response = await axios.post(`${TEAM_MEMBERS_URL}/`, {
      orgId,
      createMissing,
    });

    return response.data;
  } catch (error) {
    console.error('Error syncing team members:', error);
    throw error;
  }
};

/**
 * Register webhooks for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the registration results
 */
export const registerWebhooks = async (orgId: string) => {
  try {
    const response = await axios.post(`${WEBHOOK_MANAGER_URL}/org/${orgId}/register`, {});

    return response.data;
  } catch (error) {
    console.error('Error registering webhooks:', error);
    throw error;
  }
};

/**
 * Get webhook status for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the webhook status
 */
export const getWebhookStatus = async (orgId: string) => {
  try {
    const response = await axios.get(`${WEBHOOK_MANAGER_URL}/org/${orgId}/status`);

    return response.data;
  } catch (error) {
    console.error('Error getting webhook status:', error);
    throw error;
  }
};

/**
 * List all webhooks for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the list of webhooks
 */
export const listWebhooks = async (orgId: string) => {
  try {
    const response = await axios.get(`${WEBHOOK_MANAGER_URL}/org/${orgId}/webhooks`);
    return response.data;
  } catch (error) {
    console.error('Error listing webhooks:', error);
    throw error;
  }
};

/**
 * Auto-register missing webhooks for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the auto-registration results
 */
export const autoRegisterWebhooks = async (orgId: string) => {
  try {
    const response = await axios.post(`${WEBHOOK_MANAGER_URL}/org/${orgId}/auto-register`, {});
    return response.data;
  } catch (error) {
    console.error('Error auto-registering webhooks:', error);
    throw error;
  }
};

/**
 * Register a specific webhook for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @param event The webhook event to register
 * @returns A promise that resolves to the registration result
 */
export const registerSpecificWebhook = async (orgId: string, event: string) => {
  try {
    const response = await axios.post(`${WEBHOOK_MANAGER_URL}/org/${orgId}/webhook`, { event });
    return response.data;
  } catch (error) {
    console.error('Error registering specific webhook:', error);
    throw error;
  }
};

/**
 * Update webhook URL for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @param webhookId The webhook ID
 * @param url The new URL
 * @returns A promise that resolves to the update result
 */
export const updateWebhookUrl = async (orgId: string, webhookId: number, url: string) => {
  try {
    const response = await axios.put(`${WEBHOOK_MANAGER_URL}/org/${orgId}/webhook/${webhookId}`, {
      url,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating webhook URL:', error);
    throw error;
  }
};

/**
 * Delete a specific webhook for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @param webhookId The webhook ID
 * @returns A promise that resolves to the deletion result
 */
export const deleteWebhook = async (orgId: string, webhookId: number) => {
  try {
    const response = await axios.delete(`${WEBHOOK_MANAGER_URL}/org/${orgId}/webhook/${webhookId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting webhook:', error);
    throw error;
  }
};

/**
 * Cleanup inactive webhooks for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the cleanup result
 */
export const cleanupInactiveWebhooks = async (orgId: string) => {
  try {
    const response = await axios.delete(`${WEBHOOK_MANAGER_URL}/org/${orgId}/cleanup`);
    return response.data;
  } catch (error) {
    console.error('Error cleaning up inactive webhooks:', error);
    throw error;
  }
};

/**
 * Migrate webhooks to new URLs for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the migration result
 */
export const migrateWebhooks = async (orgId: string) => {
  try {
    const response = await axios.post(`${WEBHOOK_MANAGER_URL}/org/${orgId}/migrate`, {});
    return response.data;
  } catch (error) {
    console.error('Error migrating webhooks:', error);
    throw error;
  }
};

/**
 * Unregister webhooks for Follow Up Boss for an organization
 * @param orgId The organization ID
 * @returns A promise that resolves to the unregistration results
 */
export const unregisterWebhooks = async (orgId: string) => {
  try {
    const response = await axios.post(`${WEBHOOK_MANAGER_URL}/org/${orgId}/unregister`, {});

    return response.data;
  } catch (error) {
    console.error('Error unregistering webhooks:', error);
    throw error;
  }
};

/**
 * Update organization with FUB setup data
 * @param orgId The organization ID
 * @param setupData The setup data to save
 * @returns A promise that resolves to a boolean indicating success
 */
export const updateFubSetupData = async (
  orgId: string,
  setupData: { customfields?: string[]; allowedStages?: string[] }
): Promise<boolean> => {
  try {
    // Import firebase functions
    const { db } = await import('../lib/firebase');
    const { doc, updateDoc, getDoc } = await import('firebase/firestore');

    // Get existing fubSetup data to preserve other fields
    const orgRef = doc(db, 'Organizations', orgId);
    const orgDoc = await getDoc(orgRef);
    const orgData = orgDoc.data();
    const existingFubSetup = orgData?.fubSetup || {};

    await updateDoc(orgRef, {
      fubSetup: {
        ...existingFubSetup,
        ...setupData,
      },
    });

    return true;
  } catch (error) {
    console.error('Error updating FUB setup data:', error);
    return false;
  }
};

// Note: The generateFollowUpBossApiKey function has been removed as we now use storeFollowUpBossApiKey directly
// with user-provided API keys instead of generating random keys
